import PaymentHelper from './helper/payment-helper';
import { isVersionHigher } from './utils';
import {
  RequestBookmarksCommand,
  CheckRebootCommand
} from 'kaistore-post-messenger/src/commands';
import { MessageSender } from 'web-message-helper';
import { APP_ORIGIN, INTERNAL_APP, PATH } from './constant';
import { route } from 'preact-router';
import Application from './model/Application';
import { mozAppEvent as constMozAppEvent } from 'kaistore-post-messenger/lib/constants';

class AppStore {
  constructor() {
    this.init = () => {
      // Make KaiStore can go back APP detail page after user login.
      this.preparedDownloadApp = null;
      this.apps = [];

      this.kaipayUpdated = false;
      this._purchasedApps = new Map();
      this.categories = [];
      this.updatableApps = {
        system: new Map(),
        remote: new Map()
      };
      this.updateAllInProcess = {
        system: false,
        remote: false
      };
      this.hasPaidApp = false;
    };

    this.init();
  }

  get allRemoteItems() {
    return this.apps.filter(app => app.isInRemoteList && !app.isInternal);
  }

  get allRemoteApps() {
    return this.apps.filter(
      app => app.isInRemoteList && !app.isBookmark && !app.isInternal
    );
  }

  get bookmarks() {
    return this.apps.filter(app => app.isBookmark);
  }

  get apps() {
    return this._apps;
  }

  get installedApps() {
    return this.apps.filter(app => app.isInstalled);
  }

  set apps(apps) {
    this._apps = apps;
  }

  get hasUpdatableApps() {
    return this.updatableApps.system.size + this.updatableApps.remote.size > 0;
  }

  get updateAllRebootNeeded() {
    for (let [manifestURL] of this.updatableApps.system.entries()) {
      const application = this.findAppByManifest(manifestURL);
      if (application.isRebootNeeded) {
        return true;
      }
    }
    return false;
  }

  get popNextUpdatePendingSystemApp() {
    const manifest = this.updatableApps.system.keys().next().value;
    if (!manifest) {
      return false;
    }
    this.updatableApps.system.delete(manifest);
    return manifest;
  }

  get updatePendingRemoteApps() {
    return this.apps.filter(app => {
      return app.state.updatePending === true;
    });
  }

  resetStore() {
    this.init();
  }

  initRemoteApps(remoteApps) {
    if (!remoteApps) {
      return;
    }
    remoteApps.forEach(app => {
      if (!app.manifest_url) {
        console.warn(`Malformed app ${app.name} without manifest_url. Skip.`);
        return;
      }
      const application = new Application(app.manifest_url);
      if (application.setRemoteInfo(app)) {
        this.apps.push(application);
      }
    });
  }

  syncInstalledApps(installedApps) {
    if (Object.keys(installedApps).length === 0) {
      return;
    }

    Object.keys(installedApps).forEach(manifestURL => {
      const mozAPP = installedApps[manifestURL];
      const existedApplication = this.findAppByManifest(manifestURL);
      const application = existedApplication || new Application(manifestURL);

      application.mozAPP = mozAPP;
      this.syncUpdatableApp(application);
      application.checkUpdate();

      if (!existedApplication) {
        this.apps.push(application);
      }

      if (application.mozAPP.downloading) {
        this.handleInstall(application.mozAPP);
      }

      if (application.isInternal) {
        // XXX: possible problematic code since ondownloadsuccess cannot
        // be catched in remote
        // Payment App should be updated automatically
        // !!! INTERNAL_APP[0] is the uuid of kaios-pay,
        // !!! INTERNAL_APP[3] is the uuid of eventlogger.
        switch (application.id) {
          case INTERNAL_APP[0]:
            if (application.mozAPP) {
              // update KaiPay successfully and show the restart dialog.
              application.mozAPP.ondownloadsuccess = () => {
                const paymentHelper = new PaymentHelper();
                let isSupport = paymentHelper.isSupportPrice(
                  mozAPP.manifest.version
                );
                if (isSupport && window.unitPrice === null) {
                  this.kaipayUpdated = true;
                }
              };
            }
            break;
          default:
            break;
        }
      }
    });
  }

  // sync early updatable apps before waiting result from checkUpdate()
  // to minizie the change that update tab flashing in
  syncUpdatableApp(application) {
    const mozAPP = application.mozAPP;
    const hasUpdateManifest = mozAPP.updateManifest !== null;
    if (
      !hasUpdateManifest ||
      !isVersionHigher(
        mozAPP.manifest.version,
        mozAPP.updateManifest.version
      ) ||
      application.isInternal
    ) {
      return;
    }
    if (application.isNativeApp) {
      this.updatableApps.system.set(mozAPP.manifestURL);
    } else {
      this.updatableApps.remote.set(mozAPP.manifestURL);
    }
    application.updateState({ updatable: true });
  }

  syncBookmarks() {
    return new Promise((resolve, reject) => {
      MessageSender.send(new RequestBookmarksCommand(), (success, detail) => {
        if (success) {
          let bookmarksMap = new Map();
          Object.keys(detail).forEach(key => {
            bookmarksMap.set(key, detail[key]);
          });
          this.bookmarks.forEach(bookmark => {
            const url = bookmark.info.url;
            bookmark.state.pinned = bookmarksMap.has(url);
          });

          resolve({ bookmarkDBSupported: true });
        } else {
          resolve({ bookmarkDBSupported: false });
        }
      });
    });
  }

  /**
   * Fire appstore:change after sync purchased Apps completely, and
   * update App bricks.
   **/
  syncPurchasedApps() {
    const paymentHelper = new PaymentHelper();

    paymentHelper.getAllPurchased().then(
      products => {
        // Get apps from products only.
        products = products.filter(product => product.type === 1);
        products.forEach(product => {
          // related_good_id here is app ID.
          this._purchasedApps.set(product.related_good_id, {
            txId: product.transaction_id,
            createDate: product.created_date
          });
        });
        // start to sync remoteApps information
        this.allRemoteApps.forEach(app => {
          if (this._purchasedApps.has(app.id)) {
            app.purchased = true;
          }
        });
        this.publish('appstore:change');
      },
      error => {
        console.error('Cannot get purchased Apps from server.', error);
      }
    );
  }

  updateAll(type, enable = true) {
    const apps = this.updatableApps[type];
    if (apps && apps.size > 0) {
      this.updateAllInProcess[type] = enable;
      apps.forEach((value, manifest) => {
        const app = this.findAppByManifest(manifest);
        app.updateState({ updatePending: enable });
      });
    }
    if (type === 'system') {
      const nextManifest = this.popNextUpdatePendingSystemApp;
      if (nextManifest) {
        route(PATH.PAGE.URL({ manifest: nextManifest, batchUpdating: true }));
      }
    } else if (type == 'remote' && this.updatableApps.remote.size > 0) {
      const nextManifest = this.updatableApps.remote.keys().next().value;
      const app = this.findAppByManifest(nextManifest);
      if (app) {
        app.update();
      }
    }
  }

  handleUpdate(manifest, mozAppEvent) {
    switch (mozAppEvent) {
      case constMozAppEvent.ON_DOWNLOAD_APPLIED:
      case constMozAppEvent.ON_DOWNLOAD_SUCCESS:
      case constMozAppEvent.ON_DOWNLOAD_ERROR:
        if (this.updateAllInProcess.remote) {
          this.updatableApps.remote.delete(manifest);
          const nextManifest = this.updatableApps.remote.keys().next().value;
          const app = this.findAppByManifest(nextManifest);
          if (app) {
            app.update();
          } else {
            this.updateAllInProcess.remote = false;
            this.publish('appstore:change');
          }
        }
        break;
    }
  }

  findAppByName(name) {
    return this._findApps('name', name);
  }

  findAppById(id) {
    return this._findApps('id', id);
  }

  findItemById(id) {
    return this._findRemoteItem('id', id);
  }

  findAppByManifest(manifestURL) {
    return this._findApps('manifestURL', manifestURL);
  }

  findBookmarkByUrl(url) {
    return this._findBookmarkInfo('url', url);
  }

  _findApps(key, value) {
    return this.apps.find(app => {
      return app[key] === value;
    });
  }

  _findBookmarkInfo(key, value) {
    const bookmarks = this.apps.filter(app => app.isBookmark);
    return bookmarks.find(bookmark => {
      return bookmark.info[key] === value;
    });
  }

  _findRemoteItem(key, value) {
    return this.allRemoteItems.find(item => {
      return item[key] === value;
    });
  }

  publish(eventname) {
    const evt = new CustomEvent(eventname);
    window.dispatchEvent(evt);
  }

  rebootIfNeeded() {
    MessageSender.send(new CheckRebootCommand());
  }
}

export default (AppStore = new AppStore());



/** WEBPACK FOOTER **
 ** ./src/app-store.js
 **/